import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatNoDataRow } from '@angular/material/table';
import { FournisseurService } from '../shared/services/fournisseur.service';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { Subject, Subscription, takeUntil, tap } from 'rxjs';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Fournisseurs } from '../shared/interfaces/fournisseurs/fournisseurs.model';
import { EnumEnseigne } from '../shared/constants/enseigne-enum';
import { Router } from '@angular/router';
import { UpperCasePipe } from '@angular/common';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatPrefix, MatSuffix, MatLabel } from '@angular/material/form-field';
import { MatToolbar } from '@angular/material/toolbar';

@Component({
  selector: 'app-fournisseurs',
  templateUrl: './fournisseurs.component.html',
  styleUrl: './fournisseurs.component.scss',
  standalone: true,
  imports: [MatToolbar, MatFormField, MatIcon, MatPrefix, MatInput, FormsModule, MatButton, MatIconButton, MatSuffix, ReactiveFormsModule, MatLabel, MatSelect, MatOption, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatNoDataRow, MatPaginator, UpperCasePipe]
})
export class FournisseursComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  subscription?: Subscription;
  // Table
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  displayedColumns: string[] = ['libelle', 'enseigne', 'idLaboEnseigne', 'actions'];
  dataSource = new MatTableDataSource<Fournisseurs>();
  // Filters
  filtersForm = new FormGroup({
    enseigne: new FormControl(),
  });
  filteredValues = {
    enseigne: ''
  }
  globalFilter = '';
  enseigneFilter!: string[];
  // list fournisseur
  listFournisseurs!: Fournisseurs[];

  constructor(private router: Router, private fournisseurService: FournisseurService) { }

  ngOnInit(): void {
    this.fetchAllLaboEnseigne();
    this.onTabSelected(0);
    this.subscription = this.filtersForm.valueChanges.subscribe(value => {
      const enseigne = value.enseigne ?? '';
      this.filteredValues = { enseigne };
      this.dataSource.filter = JSON.stringify(this.filteredValues);
    });
    this.dataSource.filterPredicate = this.customFilterPredicate();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  /**
   * Called when the component is destroyed. Unsubscribes from all subscriptions to prevent memory leaks.
   */
  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
    this.subscription?.unsubscribe();
  }

  /**
   * Retrieves the list of all suppliers via the supplierService service.
   * The data is stored in the listLournisseurs property.
   */
  private fetchAllLaboEnseigne() {
    this.fournisseurService.fetchAllLaboEnseigne().pipe(
      takeUntil(this._destroying$),
      tap((data: Fournisseurs[]) => {
        this.listFournisseurs = data;
        this.manageDataSourceTable(this.listFournisseurs);
        this.getEnseigneFilters();
      })
    ).subscribe();
  }

  /**
   * Sets the array of data as the source for a data table called "dataSource".
   * @param data An array of data to set as the source for the data table.
   */
  private manageDataSourceTable(data: Fournisseurs[]) {
    if (data) {
      data.forEach(fournisseur => {
        fournisseur.enseigne = EnumEnseigne[fournisseur.enseigne as number];
      })
      this.dataSource.data = data;
    }
  }

  /**
   * Manages the filters to apply for the search bar
   * If the table has pagination then the pagination is placed on the first page
   * @param filter The value of the applied filter
   */
  applyFilter(filter: string) {
    this.globalFilter = filter;
    this.dataSource.filter = JSON.stringify(this.filteredValues);

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  /**
  * Manage custom filters against [ngModel] and filters by comparing values ​​in order to match
  * @returns boolean
  */
  private customFilterPredicate() {
    const myFilterPredicate = (data: Fournisseurs, filter: string): boolean => {
      let globalMatch = !this.globalFilter;
      if (this.globalFilter) {
        Object.values(data).forEach((value: any) => {
          if (value !== null && value.toString().trim().toLowerCase().indexOf(this.globalFilter.toLowerCase()) !== -1) {
            globalMatch = true;
          }
        });
      }
      if (!globalMatch) {
        return false;
      }
      let searchString = JSON.parse(filter);
      return data.enseigne.toString().trim().toLowerCase().indexOf(searchString.enseigne?.toLowerCase()) !== -1
    }
    return myFilterPredicate;
  }

  /**
   * Reset all filters (search bar, global filters and filtered table)
   */
  clearAllFilters() {
    this.globalFilter = '';
    this.dataSource.filter = '';
    this.filtersForm.reset();
  }

  /**
   * Reset enseigne filter
   */
  clearEnseigneFilter(event: any) {
    this.filtersForm.get('enseigne')?.setValue('');
    event.stopPropagation();
  }

  /**
   * navigate to fournisseurs-B2C view 
   * @param row Data of a selected row
   */
  goToSupplierB2C(row: Fournisseurs) {
    this.router.navigate(['fournisseurs/fournisseurs-user/'], { state: { data: row } });
  }

  /**
  * Populates the enseigneFilter array with unique enseigne values from the listFournisseurs array.
  * Sorts enseigneFilter in descending order and sets the enseigne form control to the first value.
  * This function is intended to be used as a private helper method.
  */
  private getEnseigneFilters() {
    let enseignes: string[] = [];
    this.listFournisseurs.forEach(fournisseur => {
      enseignes.push(fournisseur.enseigne as string);
      enseignes = this.removeDuplicates(enseignes);
    });
    this.enseigneFilter = enseignes;
    this.enseigneFilter.sort((a, b) => b.localeCompare(a));
  }

  /**
   * Removes duplicates from an array of elements and returns a new array containing only unique elements.
   * @param elements An array of elements with potential duplicates.
   * @returns A new array of elements with duplicates removed.
   */
  private removeDuplicates(elements: string[]): string[] {
    // Uses the Set object to create a new set of unique values from the input array,
    // and then converts it back to an array using the Array.from() method.
    return Array.from(new Set(elements));
  }

  /**
   * Highlights the selected tab and sets the others to the default state
   * @param {number} index - The index of the selected tab
   */
  onTabSelected(index: number): void {
    const menuElements = document.querySelectorAll('.menu-element');
    if (menuElements.length > 3 && index >= 0 && index < menuElements.length) {
      for (let i = 0; i < menuElements.length; i++) {
        const menuElement = menuElements[i] as HTMLElement;
        if (i === index) {
          menuElement.classList.remove('menu-element--default');
          menuElement.classList.add('menu-element--highlighted');
        } else {
          menuElement.classList.remove('menu-element--highlighted');
          menuElement.classList.add('menu-element--default');
        }
      }
    }
  }
}
